/** Shell: login gate, top bar with the two tabs (+ admin), the views (kept mounted so each keeps its state), modals. */
import { useEffect, useState } from "react";
import { FULL_FEATURES, totals } from "@ic/engine";
import { useStore, useHasSheet } from "./store.ts";
import { okToReplace } from "./shared/unsaved.ts";
import { busyBatch } from "./features/ai-estimate/batch.ts";
import { MyJobs } from "./features/jobs/MyJobs.tsx";
import { loadCoefPrefs } from "./features/cost-sheet/coef.ts";
import { useT } from "./shared/i18n.ts";
import { api } from "./shared/api.ts";
import { dropLegacyKey } from "./shared/storage.ts";
import { GlobalHeader } from "./shared/ui/ProfitVisibility.tsx";
import { Toast, Tooltip } from "./shared/ui/common.tsx";
import { AiTab } from "./features/ai-estimate/AiTab.tsx";
import { BomTab } from "./features/bom-search/BomTab.tsx";
import { ApplyDialog } from "./features/bom-search/ApplyDialog.tsx";
import { SheetView } from "./features/cost-sheet/SheetView.tsx";
import { Lookup } from "./features/cost-sheet/Lookup.tsx";
import { LineAi } from "./features/cost-sheet/LineAi.tsx";
import { MySheets } from "./features/cost-sheet/MySheets.tsx";
import { Settings } from "./features/settings/Settings.tsx";
import { Login } from "./features/auth/Login.tsx";
import { Admin } from "./features/admin/Admin.tsx";
import { FabricTool } from "./features/fabric/FabricTool.tsx";
import { GroupView } from "./features/group/GroupView.tsx";
import { useGroup } from "./features/group/groupStore.ts";
import { goSafe } from "./features/group/nav.ts";
import { flagStale, STALE_MSG } from "./shared/stale.ts";
import { platformSerial, platformSheetId, withoutPlatformSerial } from "./shared/platform-result.ts";
import type { Tool, User } from "./shared/api.ts";

/** Which tool this address is for: /fabric… = the fabric price tool (JF 2026-10-08), everything else = AutoCosting. */
const toolOfPath = (): Tool => (location.pathname.startsWith("/fabric") ? "fabric" : "costing");
const PATHS: Record<Tool, string> = { costing: "/", fabric: "/fabric" };
/** The tool to open for this user: the one the address asks for if allowed, else the one they have. */
function pickTool(u: User): Tool | null {
  const want = toolOfPath();
  if (u.tools.includes(want)) return want;
  const other = u.tools[0] ?? null;
  if (other) history.replaceState(null, "", PATHS[other]);
  return other;
}

// test hook used by the e2e suite (same name as the original's window.IC)
(window as unknown as { IC: unknown }).IC = { state: () => useStore.getState().sheet, totals: () => totals(useStore.getState().sheet) };

/** Login gate: asks the server who is logged in; shows the login (or first-admin setup) form until someone is. */
export function App() {
  const user = useStore((s) => s.user);
  const [state, setState] = useState<"loading" | "login" | "setup" | "down">("loading");
  const [sso, setSso] = useState(false);
  const check = () => api.authState().then((r) => { useStore.getState().setUser(r.user); setSso(!!r.sso); setState(r.user ? "loading" : r.setup ? "setup" : "login"); })
    .catch(() => setState("down"));
  useEffect(() => {
    let tries = 0, timer = 0;
    // the API may still be starting (`pnpm dev` starts both at once) — retry for ~30 s
    const first = () => api.authState().then((r) => { useStore.getState().setUser(r.user); setSso(!!r.sso); setState(r.user ? "loading" : r.setup ? "setup" : "login"); })
      .catch(() => { if (++tries < 20) timer = window.setTimeout(first, 1500); else setState("down"); });
    first();
    const out = () => { useStore.getState().setUser(null); check(); };
    window.addEventListener("ic:logged-out", out);
    return () => { clearTimeout(timer); window.removeEventListener("ic:logged-out", out); };
  }, []);
  if (user) {
    const tool = pickTool(user);
    if (tool === "fabric") return <FabricMain />;
    if (tool === "costing") return <Main />;
    return <div className="login"><div className="panel">{"这个账户还没有开通任何工具——请联系管理员。/ This account has no tool yet — ask the administrator."}</div></div>;
  }
  if (state === "login" || state === "setup") return <><Login setup={state === "setup"} sso={sso} onDone={() => setState("loading")} title={toolOfPath() === "fabric" ? "Fabric Price · 面料询价" : undefined} /><Toast /></>;
  return <div className="login"><div className="panel dim">{state === "down" ? "连不上服务器——请联系 IT。/ The server is not reachable — ask IT." : "…"}</div></div>;
}

function Main() {
  const t = useT();
  const { lang, view, meta, modal, sheet, user, cur, stale } = useStore();
  const compareOn = useStore((s) => s.compare.on);
  const { setLang, go, setMeta, openModal, toast, setCur } = useStore.getState();
  const has = useHasSheet();
  async function logout() {
    if (!okToReplace()) return;
    await api.logout().catch(() => undefined);
    window.dispatchEvent(new Event("ic:logged-out"));
  }

  // closing / reloading the tab with unsaved changes: the browser asks first (JF 2026-10-13)
  useEffect(() => {
    const onLeave = (e: BeforeUnloadEvent) => { if (useStore.getState().isDirty() || useGroup.getState().dirtyCount() > 0 || busyBatch()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, []);

  useEffect(() => {
    // the API may still be starting (e.g. `pnpm dev` starts both at once) — retry for ~30 s before complaining
    let tries = 0, timer = 0;
    const load = () => api.meta().then((m) => { setMeta(m); loadCoefPrefs().catch(() => undefined); }).catch(() => {
      if (++tries < 20) timer = window.setTimeout(load, 1500);
      else toast(t("The server is not reachable — ask IT.", "连不上服务器——请联系 IT。"));
    });
    load();
    if (dropLegacyKey()) toast(t("The API key is now kept on the server — removed from this browser.", "API key 现在放在服务器上，已从本浏览器删除。"));
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const serial = platformSerial(window.location.search);
    const sheetId = platformSheetId(window.location.search);
    if ((!serial && !sheetId) || !okToReplace()) return;
    let active = true;
    const load = sheetId ? api.sheet(sheetId).then((r) => ({ sheet: r.data, id: r.mine ? r.id : null }))
      : api.platformResult(serial!, new URLSearchParams(window.location.search).get("ownerEmail") ?? undefined).then((r) => ({ sheet: r.data.sheet, id: r.data.mine ? r.data.sheetId ?? null : null }));
    load.then((result) => {
      if (!active) return;
      const loaded = result.sheet;
      useStore.getState().setSheet(loaded, { id: result.id });
      useStore.getState().markSaved(loaded);
      useStore.getState().go("sheet");
      history.replaceState(null, "", withoutPlatformSerial(new URL(window.location.href)));
      toast(t("Platform estimate loaded.", "Platform 估价结果已加载。"));
    }).catch((error: unknown) => {
      if (!active) return;
      const message = error instanceof Error ? error.message : String(error);
      toast(t(`Could not load cost sheet ${sheetId || serial}: ${message}`, `无法加载 Cost Sheet ${sheetId || serial}：${message}`));
    });
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  // a new version deployed while this page was open: its code files are gone — ask to refresh (JF 2026-10-14)
  useEffect(() => {
    const pre = (e: Event) => { e.preventDefault(); useStore.getState().setStale(true); };
    const rej = (e: PromiseRejectionEvent) => { flagStale(e.reason); };
    window.addEventListener("vite:preloadError", pre);
    window.addEventListener("unhandledrejection", rej);
    return () => { window.removeEventListener("vite:preloadError", pre); window.removeEventListener("unhandledrejection", rej); };
  }, []);

  const tabOn = view === "sheet" ? sheet.from || "ai" : view;
  const ai = meta?.ai;
  return (
    <>
      <GlobalHeader><div className="top">
        <h1>Instant Costing</h1><span className="tag">{t("Internal", "内部版")}</span>
        <div className="tabs">
          <button id="tab-ai" className={tabOn === "ai" ? "on" : ""} onClick={() => go("ai")}>
            <span>{t("AI estimate", "AI 估价")}</span><small>{t("from the tech pack", "从技术包开始")}</small>
          </button>
          <button id="tab-bom" className={tabOn === "bom" ? "on" : ""} onClick={() => go("bom")}>
            <span>{t("Historical BOM", "历史 BOM")}</span><small>{t("start from a style we made", "从搜索历史款开始")}</small>
          </button>
          {/* several styles side by side (JF 2026-10-13) */}
          <button id="tab-group" className={tabOn === "group" ? "on" : ""} onClick={() => { goSafe("group"); }}>
            <span>{t("Style groups", "款式组合")}</span><small>{t("several styles side by side", "多款横向对比、一起导出")}</small>
          </button>
        </div>
        <div className="sp">
          {/* every saved sheet, from any page — people could not find it on the cost sheet page (JF 2026-10-16) */}
          {user?.tools.includes("costing") && <button className="btn mine" id="topMySheets" onClick={() => openModal({ kind: "mysheets" })}
            title={t("Every cost sheet you saved: open one to keep working on it", "你保存过的所有成本表：点开继续改")}>📁 {t("My cost sheets", "我的 Cost Sheet")}</button>}
          {view !== "sheet" && has && <button className="btn sm sheetbtn" id="openSheet" onClick={() => { goSafe("sheet"); }}>{t("Current cost sheet →", "当前 Cost sheet →")}</button>}
          {user?.tools.includes("costing") && <MyJobs />}
          <span className={"chip" + (ai?.configured ? " ok" : "")} id="aiChip">{ai?.configured ? t("AI connected", "AI 已连接") : t("AI not configured", "AI 未配置")}</span>
          {user?.tools.includes("fabric") && <a className="btn sm" id="toFabric" href="/fabric">{t("Fabric price →", "面料询价 →")}</a>}
          {user?.role === "admin" && <button className={"btn sm" + (view === "admin" ? " p" : "")} id="adminBtn" onClick={() => go("admin")}>{t("Admin", "后台")}</button>}
          <span className="who" id="whoami" title={user?.username}>{user?.displayName}</span>
          <button className="btn sm" id="logoutBtn" onClick={logout}>{t("Log out", "退出")}</button>
          {/* amounts shown and typed in USD or RMB (JF 2026-10-13); the rate is on each cost sheet */}
          <span className="seg cur" id="curSeg" data-tipx={t("Show and type amounts in USD or RMB (at the cost sheet's rate)", "金额用美元还是人民币显示和填写（按成本表上的汇率）")}>
            <button className={cur === "USD" ? "on" : ""} data-cur="USD" onClick={() => setCur("USD")}>$ USD</button>
            <button className={cur === "CNY" ? "on" : ""} data-cur="CNY" onClick={() => setCur("CNY")}>¥ RMB</button></span>
          <button className="btn sm" id="langBtn" onClick={() => setLang(lang === "zh" ? "en" : "zh")}>{lang === "zh" ? "EN" : "中文"}</button>
          <button className="btn sm" id="setBtn" onClick={() => openModal({ kind: "settings" })} title={FULL_FEATURES ? t("My default coefficients (GP), exchange rate, display", "我的默认报价系数（GP）、汇率、显示") : t("Exchange rate and display", "汇率与显示设置")}>{t("My settings", "我的设置")}</button>
        </div>
      </div></GlobalHeader>
      {stale && <div className="stalebar" id="staleBar">{STALE_MSG} <button className="btn sm p" onClick={() => location.reload()}>{t("Refresh", "刷新")}</button></div>}
      <div className={"page" + (view === "bom" || view === "admin" || view === "group" || (view === "sheet" && compareOn) ? " wide" : "")} id="page">
        <section className={"view" + (view === "ai" ? " on" : "")} id="v-ai"><AiTab /></section>
        <section className={"view" + (view === "bom" ? " on" : "")} id="v-bom"><BomTab active={view === "bom"} /></section>
        <section className={"view" + (view === "sheet" ? " on" : "")} id="v-sheet"><SheetView /></section>
        <section className={"view" + (view === "group" ? " on" : "")} id="v-group"><GroupView /></section>
        {view === "admin" && user?.role === "admin" && <section className="view on" id="v-admin"><Admin tool="costing" /></section>}
      </div>
      {modal?.kind === "settings" && <Settings />}
        {FULL_FEATURES && modal?.kind === "lookup" && <Lookup line={modal.line} i={modal.i} />}
      {modal?.kind === "lineai" && <LineAi line={modal.line} i={modal.i} />}
      {modal?.kind === "mysheets" && <MySheets />}
      {modal?.kind === "apply" && <ApplyDialog sc={modal.sc} />}
      <Tooltip key={sheet.quote ? "quote" : "cost"} /><Toast />
    </>
  );
}

/** Shell of the fabric price tool: its own title, the tool page, and the admin page scoped to it. */
function FabricMain() {
  const t = useT();
  const { lang, user } = useStore();
  const { setLang } = useStore.getState();
  const [view, setView] = useState<"tool" | "admin">("tool");
  useEffect(() => { document.documentElement.lang = lang; document.title = "Fabric Price · 面料询价"; }, [lang]);
  async function logout() {
    await api.logout().catch(() => undefined);
    window.dispatchEvent(new Event("ic:logged-out"));
  }
  return (
    <>
      <GlobalHeader><div className="top">
        <h1>{t("Fabric Price", "面料询价")}</h1><span className="tag">{t("Material development", "面料开发")}</span>
        <div className="sp">
          {user?.tools.includes("costing") && <a className="btn sm" id="toCosting" href="/">{t("Instant Costing →", "AutoCosting →")}</a>}
          {user?.role === "admin" && <button className={"btn sm" + (view === "admin" ? " p" : "")} id="adminBtn" onClick={() => setView(view === "admin" ? "tool" : "admin")}>{view === "admin" ? t("Back to the tool", "返回工具") : t("Admin", "后台")}</button>}
          <span className="who" id="whoami" title={user?.username}>{user?.displayName}</span>
          <button className="btn sm" id="logoutBtn" onClick={logout}>{t("Log out", "退出")}</button>
          <button className="btn sm" id="langBtn" onClick={() => setLang(lang === "zh" ? "en" : "zh")}>{lang === "zh" ? "EN" : "中文"}</button>
        </div>
      </div></GlobalHeader>
      <div className="page wide" id="page">
        {view === "tool" ? <section className="view on" id="v-fabric"><FabricTool /></section> : <section className="view on" id="v-admin"><Admin tool="fabric" /></section>}
      </div>
      <Tooltip /><Toast />
    </>
  );
}
